import { useEffect, useState } from 'react';
import { api, type EventoPartido, type MotivoAnulacion, type Partido, type Yo } from '../services/api';
import { Aviso, Boton, BotonVolver, Cargando, Tarjeta, Titulo } from '../components/ui';
import { MarcadorPartido } from '../components/MarcadorPartido';
import { diasEntre, fechaHora } from '../nucleo/fechas';

/**
 * Un partido por dentro: quién lo cargó, a qué hora, y quién lo confirmó,
 * rechazó o anuló.
 *
 * Existe para que un partido cargado sin jugarse se pueda identificar. No
 * acusa a nadie: muestra lo que quedó registrado y deja que el grupo saque la
 * conclusión. Lo único que se señala en color es que se haya cargado varios
 * días después de la fecha que dice, porque es lo raro.
 */
export function DetallePartido({
  partidoId,
  yo,
  onCambio,
  onVolver,
}: {
  partidoId: number;
  yo: Yo;
  onCambio: () => Promise<void>;
  onVolver: () => void;
}) {
  const [datos, setDatos] = useState<{ partido: Partido; eventos: EventoPartido[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [porAnular, setPorAnular] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [errorAccion, setErrorAccion] = useState<string | null>(null);

  useEffect(() => {
    setDatos(null);
    setError(null);
    setPorAnular(false);
    setErrorAccion(null);
    api
      .partido(partidoId)
      .then(setDatos)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Error'));
  }, [partidoId]);

  async function anular(motivo: MotivoAnulacion) {
    setOcupado(true);
    setErrorAccion(null);
    try {
      await api.anular(partidoId, motivo);
      setPorAnular(false);
      await onCambio();
      setDatos(await api.partido(partidoId));
    } catch (e) {
      setErrorAccion(e instanceof Error ? e.message : 'No se pudo anular.');
    } finally {
      setOcupado(false);
    }
  }

  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <BotonVolver onClick={onVolver} />
        <Aviso tono="error">{error}</Aviso>
      </div>
    );
  }
  if (!datos) return <Cargando que="Buscando el partido" />;

  const { partido, eventos } = datos;
  const jugado = fechaHora(Date.parse(`${partido.jugado_el}T15:00:00Z`) / 1000);
  const cargado = fechaHora(partido.creado_en);
  const demora = diasEntre(partido.jugado_el, cargado.fecha);
  const cargoSinJugar = !partido.jugadores.some((j) => j.id === partido.cargado_por);

  return (
    <div className="flex flex-col gap-4">
      <BotonVolver onClick={onVolver} />

      <div>
        <Titulo>Partido del {jugado.dia}</Titulo>
        <p className="text-sm text-(--color-apagado)">
          {partido.orden}º partido de ese día · #{partido.id}
        </p>
      </div>

      <Tarjeta compacta>
        <MarcadorPartido partido={partido} yoId={yo.jugador.id} />
      </Tarjeta>

      {yo.jugador.rol === 'admin' && (partido.estado === 'pendiente' || partido.estado === 'confirmado') && (
        <Tarjeta className="flex flex-col gap-2">
          <Titulo chico>Administrar partido</Titulo>
          <p className="text-sm text-(--color-apagado)">Si se cargó por error, anulalo. Deja de contar en las tablas y conserva su historia.</p>
          {porAnular ? <>
            <p className="font-semibold">¿Por qué lo anulás?</p>
            <div className="flex flex-wrap gap-2">
              <Boton chico tipo="peligro" deshabilitado={ocupado} onClick={() => void anular('duplicado')}>Estaba duplicado</Boton>
              <Boton chico tipo="peligro" deshabilitado={ocupado} onClick={() => void anular('otro')}>Otro motivo</Boton>
              <Boton chico tipo="secundario" deshabilitado={ocupado} onClick={() => setPorAnular(false)}>Cancelar</Boton>
            </div>
          </> : <Boton chico tipo="peligro" deshabilitado={ocupado} onClick={() => setPorAnular(true)}>Anular partido</Boton>}
        </Tarjeta>
      )}
      {errorAccion && <Aviso tono="error">{errorAccion}</Aviso>}

      <Tarjeta>
        <p className="text-xs text-(--color-apagado)">Lo cargó</p>
        <p className="text-lg font-semibold">
          {partido.cargado_por_nombre}
          {cargoSinJugar && (
            <span className="text-sm font-normal text-(--color-apagado)"> (no jugó)</span>
          )}
        </p>
        <p className="text-sm">
          el {cargado.dia} a las <span className="font-semibold">{cargado.hora}</span>
        </p>
        <p
          className={`mt-1 text-xs ${demora >= 2 ? 'text-(--color-alerta)' : 'text-(--color-apagado)'}`}
        >
          {textoDemora(demora)}
        </p>
      </Tarjeta>

      <section className="flex flex-col gap-2">
        <Titulo chico>Qué pasó con este partido</Titulo>
        <ol className="flex flex-col">
          {eventos.map((e, i) => {
            const cuando = fechaHora(e.creado_en);
            return (
              <li key={i} className="flex gap-3">
                <span className="flex flex-col items-center" aria-hidden>
                  <span className={`mt-1.5 h-2.5 w-2.5 rounded-full ${colorDe(e.tipo)}`} />
                  {i < eventos.length - 1 && <span className="w-px flex-1 bg-(--color-borde)" />}
                </span>
                <span className="pb-3">
                  <span className="block text-sm font-semibold">{queHizo(e)}</span>
                  <span className="block text-xs text-(--color-apagado)">
                    {cuando.dia} · {cuando.hora}
                  </span>
                </span>
              </li>
            );
          })}
        </ol>
        {partido.estado === 'pendiente' && (
          <p className="text-xs text-(--color-alerta)">Todavía falta que lo confirme el rival.</p>
        )}
      </section>
    </div>
  );
}

function textoDemora(dias: number): string {
  if (dias <= 0) return 'Se cargó el mismo día que se jugó.';
  if (dias === 1) return 'Se cargó al día siguiente.';
  return `Se cargó ${dias} días después de la fecha del partido.`;
}

function queHizo(e: EventoPartido): string {
  const quien = e.nombre ?? 'Alguien';
  switch (e.tipo) {
    case 'cargado':
      return `Lo cargó ${quien}`;
    case 'confirmado':
      return e.detalle?.como === 'admin'
        ? `Lo confirmó ${quien}, como admin`
        : `Lo confirmó ${quien}`;
    case 'confirmado_auto':
      return e.detalle?.al_cierre
        ? 'Se confirmó solo al cerrar el mes'
        : 'Se confirmó solo: nadie lo revisó a tiempo';
    case 'rechazado':
      return e.detalle?.motivo ? `Lo rechazó ${quien}: “${e.detalle.motivo}”` : `Lo rechazó ${quien}`;
    case 'editado': {
      const antes = e.detalle?.antes;
      const despues = e.detalle?.despues;
      return antes && despues
        ? `${quien} corrigió el resultado: de ${antes.games_a}-${antes.games_b} a ${despues.games_a}-${despues.games_b}`
        : `Lo corrigió ${quien}`;
    }
    case 'anulado':
      return e.detalle?.motivo === 'duplicado'
        ? `Lo anuló ${quien}, por duplicado`
        : `Lo anuló ${quien}`;
  }
}

function colorDe(tipo: EventoPartido['tipo']): string {
  if (tipo === 'rechazado' || tipo === 'anulado') return 'bg-(--color-error)';
  if (tipo === 'cargado') return 'bg-(--color-apagado)';
  if (tipo === 'editado') return 'bg-(--color-alerta)';
  return 'bg-(--color-acento)';
}
